// src/pages/ProfilePage.js
import React, { useState, useRef, useEffect } from 'react';
import './ProfilePage.css';
import { useNavigate } from 'react-router-dom';

import {
  getUserProfileData,
  saveUserProfileData,
  getMyOffersData,
  getSavedOffersData,
  removeSavedOfferData,
  deleteOfferData,
  uploadProfileImageData,
  clearProfileImageData,
} from "../services/profileDataService";

// 🔥 NEU: Native/Auth Service (WICHTIG)
import {
  getCurrentAuthUser,
  subscribeToAuthChanges,
  logoutUser,
} from '../services/authDataService';

// 🔔 Push (bleibt erstmal so)
import { initPushAfterLogin } from "../services/pushDataService";

// 🧠 Context & Utils
import { useAdmin } from '../context/AdminContext';
import { isNativeMobileApp, pickSingleImageNative } from "../utils/nativeImagePicker";

// 🧩 UI Components
import OfflineBanner from '../components/OfflineBanner';
import LoginHintModal from '../components/LoginHintModal';
import SpeechIcon from "../components/SpeechIcon";
import ImageSourceModal from "../components/ImageSourceModal";
import ProfileAction from "../components/ProfileAction";

function ProfilePage() {
  const navigate = useNavigate();
  const fileInputRef = useRef();

  const { isSuperAdmin } = useAdmin();

  const [profileImagePreview, setProfileImagePreview] = useState(null);
  const [profileImageURL, setProfileImageURL] = useState('');

  const [currentUser, setCurrentUser] = useState(null);
const [authChecked, setAuthChecked] = useState(false);

  const [showImageOverlay, setShowImageOverlay] = useState(false);
  const [overlayActive, setOverlayActive] = useState(false);

  const [messageToDelete, setMessageToDelete] = useState(null);

  // ✅ Edit-Modal
  const [offerToEdit, setOfferToEdit] = useState(null);

  const [myOffers, setMyOffers] = useState([]);

  const [savedOffers, setSavedOffers] = useState([]);
  const [savedOffersError, setSavedOffersError] = useState('');

  // ✅ Remove-Saved-Modal (Herz)
  const [savedToRemove, setSavedToRemove] = useState(null); // { id, title }

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    street: '',
    plz: '',
    telephone: '',
    description: '',
    systemEmailsDisabled: false,
  });

  const [profileError, setProfileError] = useState('');
  const [offersError, setOffersError] = useState('');

  const [isOffline, setIsOffline] = useState(false);
  const [retryAttempt, setRetryAttempt] = useState(0);

  const [showLoginHint, setShowLoginHint] = useState(false);

  // ✅ Profil gespeichert Modal (EE Style)
  const [showProfileSavedModal, setShowProfileSavedModal] = useState(false);

  // ✅ Push-Popup (nur Desktop)
  const [showPushModal, setShowPushModal] = useState(false);
  const [pushError, setPushError] = useState('');
  const [pushBusy, setPushBusy] = useState(false); // ✅ verhindert doppelte Klicks
  const [showImageSourceModal, setShowImageSourceModal] = useState(false);

  // ----------------------------------------------------
  // ✅ Device / Mode Checks (robust)
  // ----------------------------------------------------
  const isMobileUA = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  // ✅ Standalone/PWA Erkennung (installierte App)
  const isStandalone =
    (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
    window.navigator.standalone === true;

  // ✅ Desktop Screen check (damit Tablet/Mobile nicht nervt)
  const isDesktopScreen = window.matchMedia
    ? window.matchMedia('(min-width: 900px)').matches
    : window.innerWidth >= 900;

  // ✅ Consent Key pro User (damit mehrere Accounts nicht kollidieren)
  const currentUid = currentUser?.uid || null;
  const PUSH_CONSENT_KEY = currentUid ? `ee_push_consent_${currentUid}` : 'ee_push_consent_unknown';
  // values: "allowed" | "denied" | null

  const [actionToast, setActionToast] = useState({ visible: false, text: "" });
const actionToastTimerRef = useRef(null);

const showActionToast = (text) => {
  if (actionToastTimerRef.current) {
    clearTimeout(actionToastTimerRef.current);
  }

  setActionToast({ visible: true, text });

  actionToastTimerRef.current = setTimeout(() => {
    setActionToast({ visible: false, text: "" });
  }, 2200);
};

  // ----------------------------------------------------
  // ✅ Offline Check
  // ----------------------------------------------------
  useEffect(() => {
    const checkConnection = async () => {
      try {
        await fetch('https://www.gstatic.com/firebasejs/9.6.1/firebase-app.js', {
          method: 'HEAD',
          cache: 'no-store',
        });
        setIsOffline(false);
      } catch (err) {
        console.warn('Keine Server-Verbindung möglich (ProfilePage):', err);
        setIsOffline(true);
      }
    };

    checkConnection();
  }, [retryAttempt]);

  const handleOfflineRetry = () => {
    setIsOffline(false);
    setTimeout(() => setRetryAttempt((prev) => prev + 1), 600);
  };

useEffect(() => {
  let unsubscribeRef = null;
  let mounted = true;

  const init = async () => {
    try {
      const user = await getCurrentAuthUser();

      if (mounted) {
        setCurrentUser(user || null);
        setAuthChecked(true);
      }

      unsubscribeRef = await subscribeToAuthChanges((nextUser) => {
        if (!mounted) return;
        setCurrentUser(nextUser || null);
        setAuthChecked(true);
      });
    } catch (error) {
      console.warn("Profile auth init failed:", error);

      if (mounted) {
        setCurrentUser(null);
        setAuthChecked(true);
      }
    }
  };

  init();

  return () => {
    mounted = false;

    if (typeof unsubscribeRef === "function") {
      const maybePromise = unsubscribeRef();

      if (maybePromise?.catch) {
        maybePromise.catch((err) =>
          console.warn("Profile unsubscribe failed:", err)
        );
      }
    }
  };
}, []);
  // ----------------------------------------------------
  // ✅ Login Hint
  // ----------------------------------------------------
 useEffect(() => {
  if (!authChecked) return;
  setShowLoginHint(!currentUser);
}, [authChecked, currentUser]);

  // ----------------------------------------------------
  // ✅ Desktop Push Modal anzeigen (nur wenn noch keine Entscheidung gefallen ist)
  // ----------------------------------------------------
  useEffect(() => {
  const user = currentUser || null;
if (!user?.uid) return;

  if (!isDesktopScreen) return;
  if (isMobileUA) return;
  if (isStandalone) return;
  if (!("Notification" in window)) return;
  if (Notification.permission !== "default") return;

  const consent = localStorage.getItem(PUSH_CONSENT_KEY);
  if (consent === "allowed" || consent === "denied") return;

  if (isOffline) return;

  setShowPushModal(true);
}, [currentUser, isDesktopScreen, isMobileUA, isStandalone, isOffline, PUSH_CONSENT_KEY]);

  // Backdrop: nur schließen, NICHT speichern
  const handlePushDismiss = () => {
    if (pushBusy) return;
    setShowPushModal(false);
    setPushError('');
  };

  const handlePushDeny = () => {
    if (pushBusy) return;
    localStorage.setItem(PUSH_CONSENT_KEY, 'denied');
    setShowPushModal(false);
    setPushError('');
  };

  // ✅ NEU: "Vielleicht später" -> schließt nur, speichert nichts
  const handlePushMaybeLater = () => {
    if (pushBusy) return;
    setShowPushModal(false);
    setPushError('');
  };

  // ✅ FIX: allow flow robust + modal close garantiert
  const handlePushAllow = async () => {
    if (pushBusy) return;
    setPushBusy(true);
    setPushError('');

    try {
      if (!('Notification' in window)) {
        setPushError('Dein Browser unterstützt keine Benachrichtigungen.');
        setShowPushModal(false);
        return;
      }

      let permission = Notification.permission;

      // Permission Prompt nur, wenn "default"
      if (permission === 'default') {
        // Timeout-Schutz: manche Browser hängen sonst gefühlt „für immer“
        const permPromise = Notification.requestPermission();
        const timeoutPromise = new Promise((_, rej) =>
          setTimeout(() => rej(new Error('permission-timeout')), 12000)
        );

        permission = await Promise.race([permPromise, timeoutPromise]);
      }

      if (permission !== 'granted') {
        // User hat abgelehnt ODER Prompt geschlossen
        localStorage.setItem(PUSH_CONSENT_KEY, 'denied');
        setShowPushModal(false);
        return;
      }

      // ✅ erst jetzt als allowed speichern (wichtig!)
      localStorage.setItem(PUSH_CONSENT_KEY, 'allowed');

      // ✅ Push initialisieren (Token holen / speichern etc.)
      try {
        const res = await initPushAfterLogin({ force: true });
        console.log('🔔 Desktop Push init result:', res);

        // falls init nicht ok: kein Hard-Fail, aber optional Feedback
        if (res && res.ok === false) {
          console.warn('🔔 Push init returned not ok:', res);
        }
      } catch (e) {
        console.warn('🔔 Push init failed:', e);
      }

      setShowPushModal(false);
    } catch (e) {
      console.warn('Notification permission error:', e);
      setPushError('Konnte die Benachrichtigungen nicht aktivieren. Bitte später erneut versuchen.');
      setShowPushModal(false);
    } finally {
      setPushBusy(false);
    }
  };

  // ----------------------------------------------------
  // ✅ Profil laden
  // ----------------------------------------------------
  // ----------------------------------------------------
// ✅ Profil laden
// ----------------------------------------------------
useEffect(() => {
  const loadLocalProfile = () => {
    const userLocal = JSON.parse(localStorage.getItem('userProfile') || '{}');

    if (Object.keys(userLocal).length > 0) {
      setFormData({
        name: userLocal.name || userLocal.username || '',
        email: userLocal.email || '',
        street: userLocal.street || '',
        plz: userLocal.plz || '',
        telephone: userLocal.telephone || '',
        description: userLocal.description || '',
        systemEmailsDisabled: !!userLocal.systemEmailsDisabled,
      });

      setProfileImageURL(userLocal.profileImageURL || '');
      setProfileImagePreview(null);
      return true;
    }

    return false;
  };

  const fetchUserData = async () => {
    if (!authChecked) return;

    // Erst lokalen Stand laden, damit in Capacitor nichts leer bleibt
    loadLocalProfile();

    // Wenn Firebase-User noch nicht da ist: local fallback stehen lassen
   if (!currentUser?.uid) return;

    try {
      const data = await getUserProfileData(currentUser.uid);

if (data) {

        const loadedProfile = {
          name: data.name || data.username || '',
          email: data.email || currentUser.email || '',
          street: data.street || '',
          plz: data.plz || '',
          telephone: data.telephone || '',
          description: data.description || '',
          systemEmailsDisabled: data.systemEmailsDisabled === true,
        };

        setFormData(loadedProfile);
        setProfileImageURL(data.profileImageURL || '');
        setProfileImagePreview(null);

        localStorage.setItem(
          'userProfile',
          JSON.stringify({
            ...loadedProfile,
            profileImageURL: data.profileImageURL || '',
          })
        );
      } else {
        const existingLocal = JSON.parse(localStorage.getItem('userProfile') || '{}');

        setFormData({
          name: existingLocal.name || existingLocal.username || '',
          email: existingLocal.email || currentUser.email || '',
          street: existingLocal.street || '',
          plz: existingLocal.plz || '',
          telephone: existingLocal.telephone || '',
          description: existingLocal.description || '',
          systemEmailsDisabled: !!existingLocal.systemEmailsDisabled,
        });

        setProfileImageURL(existingLocal.profileImageURL || '');
        setProfileImagePreview(null);
      }

      setProfileError('');
    } catch (error) {
      console.error('Fehler beim Laden des Profils aus Firestore:', error);
      setProfileError('Dein Profil konnte momentan nicht geladen werden. Bitte versuche es später erneut.');

      loadLocalProfile();
    }
  };

  fetchUserData();
}, [authChecked, currentUser]);

  // ----------------------------------------------------
  // ✅ Meine Angebote laden
  // ----------------------------------------------------
 useEffect(() => {
  const fetchOffers = async () => {
    const user = currentUser;
    if (!user?.uid) return;

    try {
      const data = await getMyOffersData(user.uid);
      setMyOffers(data);
      setOffersError('');
    } catch (error) {
      console.error('Fehler beim Laden der Angebote:', error);
      setOffersError('Deine Angebote konnten momentan nicht geladen werden.');
    }
  };

  fetchOffers();
}, [currentUser]);

  // ----------------------------------------------------
  // ✅ Gespeicherte Angebote laden 
  // ----------------------------------------------------
  useEffect(() => {
  const fetchSavedOffers = async () => {
    const user = currentUser;
    if (!user?.uid) return;

    try {
      setSavedOffersError('');
      const saved = await getSavedOffersData(user.uid);
      setSavedOffers(saved);
    } catch (err) {
      console.warn('Fehler beim Laden gespeicherter Angebote:', err);
      setSavedOffersError('Gespeicherte Angebote konnten nicht geladen werden.');
      setSavedOffers([]);
    }
  };

  fetchSavedOffers();
}, [currentUser]);

  // ----------------------------------------------------
  // ✅ Form Handling
  // ----------------------------------------------------
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

 const handleSave = async () => {
  if (!currentUser?.uid) {
    setProfileError('Du bist nicht eingeloggt. Bitte logge dich erneut ein.');
    return;
  }

  const trimmedName = formData.name.trim();

  if (!trimmedName) {
    setProfileError('Bitte gib einen Namen ein, bevor du dein Profil speicherst.');
    return;
  }

  const user = currentUser;

  const savePayload = {
  ...formData,
  name: trimmedName,
  email: formData.email || user.email || '',
  profileImageURL: profileImageURL || '',
  systemEmailsDisabled: formData.systemEmailsDisabled === true,
};

localStorage.setItem('userProfile', JSON.stringify(savePayload));

try {
  await saveUserProfileData(user.uid, savePayload);
    setProfileError('');
    setShowProfileSavedModal(true);
  } catch (error) {
    console.error('Fehler beim Speichern:', error);
    setProfileError('Dein Profil konnte nicht gespeichert werden.');
  }
};

  const handleImageChange = async (e) => {
  let file = e?.target?.files?.[0] || null;

  if (!file && isNativeMobileApp()) {
    try {
      const picked = await pickSingleImageNative(e?.source || "gallery");
      if (!picked?.dataUrl) return;

      const response = await fetch(picked.dataUrl);
      const blob = await response.blob();
      file = new File([blob], `profile-${Date.now()}.jpg`, {
        type: blob.type || "image/jpeg",
      });
    } catch (err) {
      console.warn("Native Bildauswahl abgebrochen:", err);
      return;
    }
  }

  if (!file) return;

  const preview = URL.createObjectURL(file);
  setProfileImagePreview(preview);

  if (!currentUser?.uid) {
    setProfileError("Du bist nicht eingeloggt. Bitte logge dich erneut ein.");
    return;
  }

  const user = currentUser;

  try {
    const downloadURL = await uploadProfileImageData(user.uid, file);

    setProfileImageURL(downloadURL);
    setProfileImagePreview(null);

    const existingLocal = JSON.parse(localStorage.getItem("userProfile") || "{}");
    localStorage.setItem(
      "userProfile",
      JSON.stringify({ ...existingLocal, profileImageURL: downloadURL })
    );

    setProfileError("");
  } catch (error) {
    console.error("Fehler beim Hochladen:", error);
    setProfileError("Dein Profilbild konnte nicht hochgeladen werden.");
  }
};

const triggerFileSelect = async () => {
  if (isNativeMobileApp()) {
    setShowImageSourceModal(true);
    return;
  }

  if (!fileInputRef.current) return;

  const input = fileInputRef.current;
  input.setAttribute("accept", "image/*");
  input.removeAttribute("capture");
  input.value = null;

  requestAnimationFrame(() => {
    input.click();
  });
};

const handleChooseCamera = () => {
  setShowImageSourceModal(false);
  handleImageChange({ source: "camera" });
};

const handleChooseGallery = () => {
  setShowImageSourceModal(false);
  handleImageChange({ source: "gallery" });
};

  const removeProfileImage = async () => {
  setProfileImagePreview(null);
  setProfileImageURL('');

  if (!currentUser?.uid) return;
  const user = currentUser;

  try {
    await clearProfileImageData(user.uid);

    const existingLocal = JSON.parse(localStorage.getItem('userProfile') || '{}');
    localStorage.setItem(
      'userProfile',
      JSON.stringify({ ...existingLocal, profileImageURL: '' })
    );
  } catch (error) {
    console.warn('Fehler beim Entfernen des Profilbild-Eintrags:', error);
  }
};

  // ----------------------------------------------------
  // ✅ Angebot Aktionen
  // ----------------------------------------------------
  const confirmDelete = async () => {
  if (!currentUser?.uid) {
    setOffersError('Du bist nicht eingeloggt. Bitte logge dich erneut ein.');
    return;
  }

  const user = currentUser;

  try {
    await deleteOfferData(user.uid, messageToDelete);

    setMyOffers((prev) => prev.filter((offer) => offer.id !== messageToDelete));
    setMessageToDelete(null);
    setOffersError('');
  } catch (error) {
    console.error('Fehler beim Löschen:', error);
    setOffersError('Das Angebot konnte nicht gelöscht werden.');
    setMessageToDelete(null);
  }
};

  const openOfferDetails = (id) => {
    navigate(`/angebot/${id}`);
  };

  const requestEditOffer = (offer) => {
    setOfferToEdit(offer);
  };

  const confirmEditOffer = () => {
    if (!offerToEdit?.id) {
      setOfferToEdit(null);
      return;
    }
    navigate('/angebot-erstellen', { state: { editOfferId: offerToEdit.id } });
    setOfferToEdit(null);
  };

  const requestRemoveSavedOffer = (offer) => {
    setSavedToRemove({ id: offer?.id, title: offer?.title || '' });
  };

 const confirmRemoveSavedOffer = async () => {
  const offerId = savedToRemove?.id;
  if (!offerId) {
    setSavedToRemove(null);
    return;
  }

  try {
    const user = currentUser || null;
    if (!user?.uid) {
      setSavedToRemove(null);
      return;
    }

    await removeSavedOfferData(user.uid, offerId);

    setSavedOffers((prev) => prev.filter((o) => String(o.id) !== String(offerId)));
  } catch (err) {
    console.warn('Konnte gespeichertes Angebot nicht entfernen:', err);
  } finally {
    setSavedToRemove(null);
  }
};

  // ----------------------------------------------------
  // ✅ Render
  // ----------------------------------------------------
  return (
    <div className="ProfilePage">
      {isOffline && <OfflineBanner onRetry={handleOfflineRetry} />}
      {showLoginHint && <LoginHintModal onClose={() => setShowLoginHint(false)} />}

      <img
        src="/logo-edens-ernte-transparent.png"
        alt="Logo"
        className="browse-logo"
        onClick={() => navigate('/')}
      />
      <SpeechIcon className="speech-icon no-tap-highlight" />
      <img
        src="/logout-icon.png"
        alt="Logout"
        className="logout-icon"
        onClick={async () => {
  try {
    await logoutUser();
    localStorage.removeItem("userProfile");
  } catch (err) {
    console.warn("Fehler beim Logout:", err);
  }
  navigate("/login");
}}
      />
      <img src="/rezept-icon.png" alt="Rezepthelfer Icon" className="rezept-icon" onClick={() => navigate('/rezepte')} />
      <p className="top-claim">Nachhaltigkeit – ganz einfach!</p>

      <div className="profile-content">
  {actionToast.visible && (
    <div className="mainbox-toast">
      {actionToast.text}
    </div>
  )}
       <div className="profile-header-row">
  <h2 className="profile-title">Mein Bereich</h2>

  <div className="profile-header-actions">
    {isSuperAdmin && (
      <button
        type="button"
        className="admin-link-btn"
        title="Admin Bereich"
        onClick={() => navigate("/admin")}
      >
        💪
      </button>
    )}

    <ProfileAction onActionToast={showActionToast} />
  </div>
</div>

        <div className="profile-image-section">
          <div className="image-ring">
            <div className="image-wrapper">
              <img
                src={profileImagePreview || profileImageURL || '/default-profile.png'}
                alt="Profil"
                className="profile-image"
                onClick={() => {
                  if (isMobileUA) {
                    setShowImageOverlay(!showImageOverlay);
                    setOverlayActive(!overlayActive);
                  } else {
                    setShowImageOverlay(true);
                  }
                }}
              />
              {showImageOverlay && (
                <div className="image-overlay" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!overlayActive && isMobileUA) return;
                      setShowImageOverlay(false);
                      setOverlayActive(false);
                      triggerFileSelect();
                    }}
                  >
                    Bild ändern
                  </button>
                  <button
                    onClick={async (e) => {
                      e.stopPropagation();
                      if (!overlayActive && isMobileUA) return;
                      await removeProfileImage();
                      setShowImageOverlay(false);
                      setOverlayActive(false);
                    }}
                  >
                    Bild entfernen
                  </button>
                </div>
              )}
            </div>
          </div>

          <input type="file" accept="image/*" style={{
  position: "absolute",
  left: "-9999px",
  width: "1px",
  height: "1px",
  opacity: 0,
}} ref={fileInputRef} onChange={handleImageChange} />
        </div>

        <div className="form-section">
          <input type="text" name="name" placeholder="Name" value={formData.name} onChange={handleInputChange} />
          <input type="email" name="email" placeholder="E-Mail" value={formData.email} onChange={handleInputChange} />
          <input type="text" name="street" placeholder="Straße, Hausnummer" value={formData.street} onChange={handleInputChange} />
          <input type="text" name="plz" placeholder="Postleitzahl, Ort" value={formData.plz} onChange={handleInputChange} />
          <input type="text" name="telephone" placeholder="Telefonnummer" value={formData.telephone} onChange={handleInputChange} />
          <textarea name="description" placeholder="Notizen" value={formData.description} onChange={handleInputChange} />

          <label className="profile-checkbox-row">
            <input
              type="checkbox"
              checked={formData.systemEmailsDisabled === true}
              onChange={(e) => setFormData((prev) => ({ ...prev, systemEmailsDisabled: e.target.checked }))}
            />
            <span>System Emails (Benachrichtigungen) deaktivieren</span>
          </label>

          <button className="green-button" onClick={handleSave}>
            Profil speichern
          </button>

          {profileError && <div className="error-message profile-error">{profileError}</div>}
        </div>

        {/* ✅ Meine Angebote */}
        <div className="offer-section">
          <h3>Meine Angebote</h3>
          {offersError && <div className="error-message offers-error">{offersError}</div>}

          <ul>
            {myOffers.map((offer) => (
              <li key={offer.id} onClick={() => openOfferDetails(offer.id)} className="offer-li">
                <span className="offer-title">{offer.title}</span>

                <span className="offer-actions" onClick={(e) => e.stopPropagation()}>
                  <span
                    className="profil-edit-icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      requestEditOffer(offer);
                    }}
                    title="Angebot bearbeiten"
                  >
                    ✏️
                  </span>

                  <span
                    className="profil-delete-icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      setMessageToDelete(offer.id);
                    }}
                    title="Angebot löschen"
                  >
                    🗑️
                  </span>
                </span>
              </li>
            ))}
          </ul>

          {myOffers.length === 0 && !offersError && <p style={{ opacity: 0.85 }}>Noch keine Angebote erstellt.</p>}

          <button className="green-button" onClick={() => navigate('/angebot-erstellen')}>
            Angebot erstellen
          </button>
        </div>

        {/* ✅ Gespeicherte Angebote */}
        <div className="offer-section saved-offer-section">
          <h3>Gespeicherte Angebote</h3>

          {savedOffersError && <div className="error-message offers-error">{savedOffersError}</div>}

          <ul>
            {savedOffers.map((offer) => (
              <li key={offer.id} onClick={() => navigate(`/angebot/${offer.id}`)} className="offer-li">
                <span className="offer-title">{offer.title}</span>

                <span className="offer-actions" onClick={(e) => e.stopPropagation()}>
                  <span
                    className="profil-heart-icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      requestRemoveSavedOffer(offer);
                    }}
                    title="Aus gespeicherten entfernen"
                  >
                    ❤️
                  </span>
                </span>
              </li>
            ))}
          </ul>

          {savedOffers.length === 0 && !savedOffersError && <p style={{ opacity: 0.85 }}>Noch keine gespeicherten Angebote.</p>}
        </div>
      </div>

      {/* ✅ Desktop Push Modal */}
      {showPushModal && (
        <div className="modal-overlay" onClick={handlePushDismiss}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Benachrichtigungen aktivieren?</h3>
            <p>Damit du immer auf dem Laufenden bleibst, würden wir dir gerne Browser-Benachrichtigungen schicken.</p>

            {pushError && (
              <div className="error-message offers-error" style={{ marginTop: 8 }}>
                {pushError}
              </div>
            )}

            <div className="modal-buttons">
              <button className="cancel-btn" onClick={handlePushDeny} disabled={pushBusy}>
                Verbieten
              </button>

              {/* ✅ Neu */}
              <button className="green-button" onClick={handlePushMaybeLater} disabled={pushBusy}>
                Vielleicht später
              </button>

              <button className="green-button" onClick={handlePushAllow} disabled={pushBusy}>
                {pushBusy ? 'Bitte warten…' : 'Erlauben'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ Profil gespeichert Modal */}
      {showProfileSavedModal && (
        <div className="modal-overlay" onClick={() => setShowProfileSavedModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Profil gespeichert</h3>
            <p>Deine Änderungen wurden erfolgreich gespeichert.</p>
            <div className="modal-buttons">
              <button className="green-button" onClick={() => setShowProfileSavedModal(false)}>
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ Delete Modal */}
      {messageToDelete !== null && (
        <div className="modal-overlay" onClick={() => setMessageToDelete(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Angebot wirklich löschen?</h3>
            <p>Diese Aktion kann nicht rückgängig gemacht werden.</p>
            <div className="modal-buttons">
              <button className="cancel-btn" onClick={() => setMessageToDelete(null)}>
                Abbrechen
              </button>
              <button className="delete-btn" onClick={confirmDelete}>
                Löschen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ Edit Modal */}
      {offerToEdit !== null && (
        <div className="modal-overlay" onClick={() => setOfferToEdit(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Angebot bearbeiten?</h3>
            <p>Willst du dein Angebot „{offerToEdit?.title || '—'}“ bearbeiten?</p>
            <div className="modal-buttons">
              <button className="cancel-btn" onClick={() => setOfferToEdit(null)}>
                Abbrechen
              </button>
              <button className="green-button" onClick={confirmEditOffer}>
                Ja
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ Herz-Modal */}
      {savedToRemove !== null && (
        <div className="modal-overlay" onClick={() => setSavedToRemove(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Angebot wirklich entfernen?</h3>
            <p>
              Willst du das gespeicherte Angebot <strong>„{savedToRemove?.title || '—'}“</strong> entfernen?
            </p>
            <div className="modal-buttons">
              <button className="cancel-btn" onClick={() => setSavedToRemove(null)}>
                Abbrechen
              </button>
              <button className="delete-btn" onClick={confirmRemoveSavedOffer}>
                Entfernen
              </button>
              
            </div>
          </div>
        </div>
      )}
      <ImageSourceModal
  isOpen={showImageSourceModal}
  onClose={() => setShowImageSourceModal(false)}
  onCamera={handleChooseCamera}
  onGallery={handleChooseGallery}
/>
    </div>
  );
}

export default ProfilePage;